import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'destack_user_geo';

/**
 * useUserLocation hook
 *
 * Returns { position, status, error, request }
 *   position: { lat, lng } | null
 *   status: 'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable'
 *
 * Cached for 1h in localStorage so user is not asked every navigation.
 */
export const useUserLocation = (autoRequest = false) => {
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  // Try to load cached position
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.ts && Date.now() - parsed.ts < 60 * 60 * 1000) {
        setPosition({ lat: parsed.lat, lng: parsed.lng });
        setStatus('granted');
      }
    } catch {}
  }, []);

  const request = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      setError('Geolocalização não suportada neste navegador.');
      return;
    }
    setStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPosition(next);
        setStatus('granted');
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...next, ts: Date.now() }));
        } catch {}
      },
      (err) => {
        setStatus('denied');
        setError(err.message || 'Permissão negada');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    if (autoRequest && status === 'idle') request();
  }, [autoRequest, status, request]);

  return { position, status, error, request };
};

export default useUserLocation;
